import {
  FC,
  useState,
  useRef,
  ReactNode,
  useEffect,
} from 'react';
// 组件
import { ProModal, ProSpin, ProButton, ProNotification } from '../antd';
import SuperForm from '../SuperForm';
// 方法
import useFormItems from '../DetailPage/hook/useFormItems';
import { getWidth } from './utils';
// 常量
import { FormModalProps, FormModalType } from './interface';
import { FormInstance } from 'antd/lib/form';
import { SUCCESS_STATUS_CODE } from '../constants/constant';

/** 弹窗表单 */
const FormModal: FC<FormModalProps> = props => {
  const {
    fetchApi,
    actionRef,
    formItems,
    footer,
    hideOkButton,
    hideEditButton,
    size,
    hideCancelButton,
    okButtonText,
    editButtonText,
    cancelButtonText,
    defaultLineNumber,
    defaultLabelCol,
    filterEmpty,
    filterSpace,
    testId = 'form-modal',
    bodyStyle,
    maxHeight = document.body.clientHeight - 108 - 150,
    onSuccess,
    onFail,
    onDetail,
    onSubmit,
    onBeforeSubmit,
    onCancel,
    ...resProps
  } = props;
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalType, setModalType] = useState<FormModalType>('edit');
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const superFormRef = useRef<FormInstance>();
  const timer = useRef<any>();
  const formTimer = useRef<any>();
  const { currentFormItems, formItemDispatch } = useFormItems(
    formItems,
    modalType === 'detail',
  );
  useEffect(() => {
    if (!visible) superFormRef.current = undefined;
    if (visible && !superFormRef.current && actionRef) {
      formTimer.current = setInterval(() => {
        if (superFormRef.current && actionRef.current) {
          actionRef.current.form = superFormRef.current;
          clearInterval(formTimer.current);
        }
      }, 100);
    }
  }, [visible]);
  // 确定
  const handleOk = async () => {
    const values: any = await validateFields();
    if (values) superFormRef.current?.submit();
  };
  const validateFields = async () => {
    return superFormRef.current
      ?.validateFields()
      .then((values: any) => {
        return values;
      })
      .catch((errorInfo: any) => {
        if (Array.isArray(errorInfo.errorFields)) {
          const messageNode = (
            <>
              {errorInfo.errorFields.map((v: any) => (
                <div key={v.name[0]}>{v.errors[0]}</div>
              ))}
            </>
          );
          ProNotification.warning({
            message: messageNode,
          });
        }
      });
  };
  // 提交
  const handleFinish = async (values: any) => {
    if (onSubmit) {
      setConfirmLoading(true);
      await onSubmit(values);
      setConfirmLoading(false);
      return;
    }
    if (fetchApi) {
      let params = values;
      if (onBeforeSubmit) {
        const newParams = await onBeforeSubmit(values);
        if (!newParams) return;
        params = newParams;
      }
      setConfirmLoading(true);
      const res = await fetchApi(params);
      setConfirmLoading(false);
      if (res.code === SUCCESS_STATUS_CODE) {
        onSuccess && onSuccess(res);
        return;
      }
      onFail && onFail(res);
      return;
    }
    console.error('fetchApi 或 onSubmit 必须定义一个');
  };
  // 点击编辑
  const handleDetail = () => {
    setModalType('edit');
    onDetail?.();
  };
  // 点击取消
  const handleCancel = (event: any) => {
    setVisible(false);
    onCancel?.(event);
  };
  // 底部按钮
  const footerRender = () => {
    const footerButtons: ReactNode[] = [];
    if (!hideCancelButton) {
      footerButtons.push(
        <ProButton key="cancel" type="default" onClick={handleCancel}>
          {cancelButtonText}
        </ProButton>,
      );
    }
    if (modalType === 'detail' && !hideEditButton) {
      footerButtons.push(
        <ProButton key="edit" type="primary" onClick={handleDetail}>
          {editButtonText}
        </ProButton>,
      );
    }
    if (modalType === 'edit' && !hideOkButton) {
      footerButtons.push(
        <ProButton
          key="ok"
          type="primary"
          loading={confirmLoading}
          onClick={handleOk}
        >
          {okButtonText}
        </ProButton>,
      );
    }
    return footerButtons;
  };
  const handleSetFieldsValue = (values?: any) => {
    if (!superFormRef.current) {
      timer.current && clearInterval(timer.current);
      timer.current = setInterval(() => {
        if (superFormRef.current) {
          superFormRef.current?.setFieldsValue(values);
          clearInterval(timer.current);
        }
      }, 200);
      return;
    }
    superFormRef.current.setFieldsValue(values);
  };
  if (actionRef) {
    actionRef.current = {
      show: () => {
        setVisible(true)
        return actionRef.current;
      },
      hide: () => setVisible(false),
      loading: (val: boolean) => {
        setLoading(val);
        return actionRef.current;
      },
      form: superFormRef.current,
      getForm: () => {
        return superFormRef.current;
      },
      add: (values?: any) => {
        setModalType('edit');
        setVisible(true);
        formItemDispatch({
          type: 'selectTable',
          payload: { initValues: values || {} },
        });
        handleSetFieldsValue(values);
        return actionRef.current;
      },
      edit: (initValues: any) => {
        setModalType('edit');
        setVisible(true);
        formItemDispatch({ type: 'selectTable', payload: { initValues } });
        handleSetFieldsValue(initValues);
        return actionRef.current;
      },
      detail: (initValues: any) => {
        setModalType('detail');
        setVisible(true);
        formItemDispatch({ type: 'selectTable', payload: { initValues } });
        handleSetFieldsValue(initValues);
        return actionRef.current;
      },
    };
  }

  return (
    <ProModal
      {...resProps}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      footer={footer || footerRender()}
      width={getWidth(size)}
      styles={{body: {maxHeight, overflow: 'auto', ...bodyStyle}}}
      // bodyStyle={{ maxHeight, overflow: 'auto', ...bodyStyle }}
    >
      <ProSpin spinning={loading}>
        <SuperForm
          actionRef={superFormRef}
          testId={testId}
          formItems={currentFormItems}
          onFinish={handleFinish}
          defaultLineNumber={defaultLineNumber}
          defaultLabelCol={defaultLabelCol}
          filterEmpty={filterEmpty}
          filterSpace={filterSpace}
        />
      </ProSpin>
    </ProModal>
  );
};
FormModal.defaultProps = {
  hideOkButton: false,
  hideEditButton: false,
  hideCancelButton: false,
  maskClosable: false,
  okButtonText: '确定',
  cancelButtonText: '取消',
  editButtonText: '编辑',
  defaultLineNumber: 2,
  defaultLabelCol: {
    span: 8,
  },
  filterEmpty: true,
  filterSpace: 'both',
  size: 'small',
};
export default FormModal;
